import { isDefined } from "@clipboard-health/util-ts";
import { Badge, CbhIcon } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useGetEasySchedulingPlacements } from "../api/useGetEasySchedulingPlacements";

interface BadgedJobsIconProps {
  /**
   * Icon type to display
   */
  type: "briefcase" | "briefcase-colored" | "rebrand-briefcase-colored";
  /**
   * Icon size
   */
  size: "large" | "medium" | "small";
}

/**
 * Displays a briefcase icon with a red badge showing the count of easy scheduling placements.
 * Automatically fetches the count and shows/hides the badge based on availability.
 *
 * @example
 * ```tsx
 * // In navigation
 * <BadgedJobsIcon type="briefcase-colored" size="large" />
 *
 * // Inactive state
 * <BadgedJobsIcon type="briefcase" size="large" />
 * ```
 */
export function BadgedJobsIcon({ type, size }: BadgedJobsIconProps) {
  const worker = useDefinedWorker();
  const { data: placementCandidate } = useCreateOrGetPlacementCandidate({
    workerId: worker.userId,
  });

  // Fetch easy scheduling placements - uses client-side counting from first page (200 items)
  const { data: easySchedulingData } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      shouldLogEvent: false,
    },
    {
      enabled: isDefined(worker.userId) && isDefined(placementCandidate?.id),
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Count from first page - efficient since we use a large page size (200)
  const easySchedulingCount = easySchedulingData?.pages[0]?.data.length ?? 0;
  const hasEasySchedulingJobs = easySchedulingCount > 0;

  return (
    <Badge count={easySchedulingCount} color="error" invisible={!hasEasySchedulingJobs}>
      <CbhIcon type={type} size={size} />
    </Badge>
  );
}
